import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const schema = z.object({
  fullName: z.string().trim().min(2, "Name required").max(100),
  company: z.string().trim().min(2, "Company required").max(120),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().trim().min(10, "Valid phone required").max(20),
  location: z.string().trim().min(2, "City and state required").max(120),
  quantity: z.string().trim().min(1, "Quantity required").max(20),
  application: z.string().trim().min(5, "Tell us where it will work").max(1000),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please agree to be contacted about this request." }),
  }),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productName: string;
  variantLabel?: string;
}

const PuduQuoteDialog = ({ open, onOpenChange, productName, variantLabel }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const productInterest = variantLabel ? `${productName} (${variantLabel})` : productName;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      company: "",
      email: "",
      phone: "",
      location: "",
      quantity: "1",
      application: "",
      consent: false as unknown as true,
    },
  });

  useEffect(() => {
    if (open) form.clearErrors();
  }, [open, form]);

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-qualified-lead", {
        body: {
          name: data.fullName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          industry: data.location,
          robotInterest: productInterest,
          quantity: data.quantity,
          message: `Location: ${data.location}\nIntended application: ${data.application}`,
          source: `Robot Quote Request — ${productInterest}`,
        },
      });
      if (error) throw error;
      toast({
        title: "Request received",
        description: `A Neil AI specialist will follow up about ${productInterest}.`,
      });
      form.reset();
      onOpenChange(false);
    } catch {
      toast({
        title: "Submission failed",
        description: "Please try again or call (336) 962-4539.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-orbitron text-2xl">Request a Quote</DialogTitle>
          <DialogDescription>
            {productInterest} — tell us about the site and a Neil AI specialist will scope it with you.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="rounded-lg border border-[hsl(var(--line))] bg-white/5 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Product: </span>
              <span className="font-semibold">{productInterest}</span>
            </div>
            <FormField control={form.control} name="fullName" render={({ field }) => (
              <FormItem><FormLabel>Name</FormLabel><FormControl><Input placeholder="Your full name" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="company" render={({ field }) => (
              <FormItem><FormLabel>Company</FormLabel><FormControl><Input placeholder="Your company" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField control={form.control} name="email" render={({ field }) => (
                <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" placeholder="you@company.com" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="phone" render={({ field }) => (
                <FormItem><FormLabel>Phone</FormLabel><FormControl><Input type="tel" placeholder="(336) 962-4539" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField control={form.control} name="location" render={({ field }) => (
                <FormItem><FormLabel>Location</FormLabel><FormControl><Input placeholder="City, state" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
              <FormField control={form.control} name="quantity" render={({ field }) => (
                <FormItem><FormLabel>Quantity</FormLabel><FormControl><Input inputMode="numeric" placeholder="1" {...field} /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <FormField control={form.control} name="application" render={({ field }) => (
              <FormItem>
                <FormLabel>Intended application</FormLabel>
                <FormControl>
                  <Textarea rows={4} placeholder="Site type, area or acreage, shifts, surfaces or pallet types, and what you want automated." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="consent" render={({ field }) => (
              <FormItem className="flex items-start gap-3 space-y-0">
                <FormControl>
                  <Checkbox checked={!!field.value} onCheckedChange={field.onChange} aria-label="Consent to contact" />
                </FormControl>
                <div>
                  <FormLabel className="text-xs font-normal text-muted-foreground">
                    I agree that Neil AI Global Solutions may contact me about this request.
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )} />
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Submitting…" : "Send Quote Request"}
            </Button>
            <p className="text-[11px] text-muted-foreground">
              Pricing, availability and deployment terms are confirmed by a specialist after a site assessment.
            </p>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PuduQuoteDialog;
